import { useEffect, useState } from 'react';
import { Lock, ShieldCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { emptyPin, PinInput, pinArrayToString } from '@/components/user/PinInput';
import { notify, userAccountService } from '@/services';
import type { PublicUser } from '@/types/auth';
import { cn } from '@/utils/cn';

interface PinSetupModalProps {
  open: boolean;
  onClose: () => void;
  onSetLater?: () => void;
  onSuccess: (user: PublicUser) => void;
}

export function PinSetupModal({ open, onClose, onSetLater, onSuccess }: PinSetupModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [pin, setPin] = useState(emptyPin());
  const [confirmPin, setConfirmPin] = useState(emptyPin());
  const [loading, setLoading] = useState(false);

  const pinComplete = pin.every(Boolean);
  const confirmComplete = confirmPin.every(Boolean);

  useEffect(() => {
    if (open) {
      setStep(1);
      setPin(emptyPin());
      setConfirmPin(emptyPin());
    }
  }, [open]);

  function goToConfirm() {
    if (!pinComplete) return;
    setStep(2);
  }

  function goBack() {
    setStep(1);
    setConfirmPin(emptyPin());
  }

  async function handleSubmit() {
    const pinValue = pinArrayToString(pin);
    const confirmValue = pinArrayToString(confirmPin);

    if (!/^\d{4}$/.test(pinValue)) {
      notify.error('PIN must be exactly 4 digits');
      return;
    }

    if (confirmValue !== pinValue) {
      notify.error('PINs do not match. Please try again.');
      setConfirmPin(emptyPin());
      return;
    }

    setLoading(true);
    try {
      const res = await userAccountService.setTransactionPin(pinValue, confirmValue);
      if (!res.success || !res.data?.user) throw new Error(res.message || 'Failed to set PIN');
      onSuccess(res.data.user);
      notify.apiSuccess(res.message, 'Transaction PIN set successfully');
      onClose();
    } catch (err) {
      notify.apiError(err, 'Failed to set PIN');
    } finally {
      setLoading(false);
    }
  }

  const title = step === 1 ? 'Set transaction PIN' : 'Confirm PIN';
  const subtitle =
    step === 1
      ? '4-digit PIN for wallet payments'
      : 'Re-enter the same PIN';

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xs"
      hideClose
      bodyClassName="py-4"
      footer={
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full gap-2">
            {step === 2 && (
              <Button variant="outline" size="sm" className="flex-1 sm:flex-none" disabled={loading} onClick={goBack}>
                Back
              </Button>
            )}
            <Button
              size="sm"
              className="flex-1"
              loading={loading}
              loadingText={step === 1 ? 'Continuing…' : 'Saving…'}
              disabled={step === 1 ? !pinComplete : !confirmComplete}
              onClick={() => (step === 1 ? goToConfirm() : void handleSubmit())}
            >
              {step === 1 ? 'Continue' : 'Save PIN'}
            </Button>
          </div>
          {onSetLater && step === 1 && (
            <Button variant="outline" size="sm" fullWidth disabled={loading} onClick={onSetLater}>
              Set up later
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-brand text-white">
            {step === 1 ? <Lock className="h-4 w-4" strokeWidth={2.5} /> : <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />}
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-base font-bold leading-tight text-brand-navy">{title}</h3>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="-mr-1 rounded-md p-1 text-brand-muted transition hover:bg-brand-surface hover:text-brand-navy disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-0.5 text-xs text-brand-muted">{subtitle}</p>
          </div>
        </div>

        <div className="flex gap-1.5">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors',
                step >= s ? 'bg-gradient-brand' : 'bg-gray-200',
              )}
            />
          ))}
        </div>

        <div className="rounded-xl bg-brand-surface/70 px-3 py-4">
          <PinInput
            value={step === 1 ? pin : confirmPin}
            onChange={step === 1 ? setPin : setConfirmPin}
            autoFocus
            disabled={loading}
          />
        </div>
      </div>
    </Modal>
  );
}
